import { useCallback, useEffect, useState } from "react";
import SessionsApi from "../../../api/sessionApi";
import Loader from "../../../components/Loader/Loader";
import {
  SessionInfoForAdmin,
  TypeSessionFilters,
} from "../../../types/TypeSession";
import SessionsTable, {
  FilteredSession,
} from "../AwaitingRefound/SessionsTable/SessionsTable";
import SessionInfoBlock, {
  SessionForDisplay,
} from "../shared/SessionInfoBlock/SessionInfoBlock";
import s from "./AllSessions.module.css";
import SessionFilters from "./SessionFilters/SessionFilters";

const AllSessions = () => {
  const token = localStorage.getItem("token");
  const [pagination, setPagination] = useState({
    page: 0,
    rowPerPage: 10,
    total: 0,
  });
  const [sessionsData, setSessionsData] = useState<FilteredSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<
    string | undefined
  >();
  const [selectedSessionDetail, setSelectedSessionDetail] = useState<
    SessionInfoForAdmin | undefined
  >();
  const [actionLoaders, setActionLoaders] = useState({
    loadSessions: true,
    loadDetail: false,
  });
  const [activeFilters, setActiveFilters] = useState<
    Omit<TypeSessionFilters, "page" | "limit" | "asExpert">
  >({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const loadSessions = useCallback(
    async (
      page: number,
      limit: number,
      filters: Omit<TypeSessionFilters, "page" | "limit" | "asExpert">,
    ) => {
      if (!token) return;
      setActionLoaders((prev) => ({ ...prev, loadSessions: true }));
      const response = await SessionsApi.getFilteredSessions(token, {
        ...filters,
        page: page + 1,
        limit,
        asExpert: false,
      });
      setActionLoaders((prev) => ({ ...prev, loadSessions: false }));
      if (response.status && response.sessions) {
        setSessionsData(response.sessions as FilteredSession[]);
        setPagination((prev) => ({
          ...prev,
          total: response.totalAmount ?? 0,
        }));
      }
    },
    [token],
  );

  useEffect(() => {
    loadSessions(pagination.page, pagination.rowPerPage, activeFilters);
  }, [pagination.page, pagination.rowPerPage, activeFilters, loadSessions]);

  useEffect(() => {
    if (!selectedSessionId || !token) {
      setSelectedSessionDetail(undefined);
      return;
    }
    (async () => {
      setActionLoaders((prev) => ({ ...prev, loadDetail: true }));
      const response = await SessionsApi.getSessionInfoForAdmin(
        token,
        selectedSessionId,
      );

      setActionLoaders((prev) => ({ ...prev, loadDetail: false }));
      if (response.status && response.session) {
        setSelectedSessionDetail(response.session);
      }
    })();
  }, [selectedSessionId, token]);

  const handleFiltersApply = (
    filters: Omit<TypeSessionFilters, "page" | "limit" | "asExpert">,
  ) => {
    setActiveFilters(filters);
    setPagination((prev) => ({ ...prev, page: 0 }));
    setSelectedSessionId(undefined);
    setIsFiltersOpen(false);
  };

  const handleBack = () => {
    setSelectedSessionId(undefined);
    setSelectedSessionDetail(undefined);
  };

  return (
    <div className={s.container}>
      <div className={s.toolbar}>
        <button
          type="button"
          className={s.openFiltersBtn}
          onClick={() => setIsFiltersOpen(true)}
        >
          Filters
        </button>
      </div>

      <div className={s.mainContent}>
        <div
          className={
            selectedSessionId
              ? `${s.tableBlock} ${s.mobileTableBlockHidden}`
              : `${s.tableBlock} ${s.mobileTableBlockVisible}`
          }
        >
          <SessionsTable
            variant="all"
            count={pagination.total}
            page={pagination.page}
            rows={sessionsData}
            rowsPerPage={pagination.rowPerPage}
            setPagination={setPagination}
            loader={actionLoaders.loadSessions}
            selectedSessionId={selectedSessionId}
            onSelectSessionId={setSelectedSessionId}
            containerHeight="calc(100vh - 210px)"
          />
        </div>
        <div
          className={
            selectedSessionId
              ? `${s.sessionInfoBlock} ${s.sessionInfoBlockActive} ${s.mobileSessionVisible}`
              : `${s.sessionInfoBlock} ${s.mobileSessionHidden}`
          }
          style={{
            maxHeight: selectedSessionId ? "calc(100vh - 210px)" : "0px",
          }}
        >
          {selectedSessionId && (
            <>
              {actionLoaders.loadDetail ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px 0",
                  }}
                >
                  <Loader size={60} />
                </div>
              ) : selectedSessionDetail ? (
                <SessionInfoBlock
                  session={
                    selectedSessionDetail as unknown as SessionForDisplay
                  }
                  onBack={handleBack}
                />
              ) : null}
            </>
          )}
        </div>
      </div>

      <button
        type="button"
        aria-label="Close filters"
        className={`${s.drawerBackdrop} ${isFiltersOpen ? s.drawerBackdropVisible : ""}`}
        onClick={() => setIsFiltersOpen(false)}
      />
      <aside
        className={`${s.filtersDrawer} ${isFiltersOpen ? s.filtersDrawerOpen : ""}`}
      >
        <div className={s.drawerHeader}>
          <h3 className={s.drawerTitle}>Session filters</h3>
          <button
            type="button"
            className={s.closeDrawerBtn}
            onClick={() => setIsFiltersOpen(false)}
          >
            Close
          </button>
        </div>
        <div className={s.drawerContent}>
          <SessionFilters onApply={handleFiltersApply} />
        </div>
      </aside>
    </div>
  );
};

export default AllSessions;
